/**
 * usePricing Hook
 *
 * React hook for fetching real-time pricing data for investments
 * Fetches prices from Firestore /marketPrices collection
 * Provides automatic refresh and caching
 */

import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';

/**
 * Hook for fetching individual investment price
 *
 * @param {string} ticker - Stock ticker symbol
 * @param {string} assetType - Asset type (stocks, crypto, commodities)
 * @returns {Object} { price, loading, error, lastUpdated, refresh }
 */
export const useInvestmentPrice = (ticker, assetType = 'stocks') => {
  const { currentUser } = useAuth();
  const [priceData, setPriceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchPrice = useCallback(async () => {
    if (!ticker) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Normalize ticker to match Firebase function storage format (uppercase only)
      // Firebase stores under the exact ticker user entered (with suffix if present)
      const normalizedTicker = ticker.toUpperCase().trim();
      // Check if this is a UK stock (has .L suffix) - prices will be in pence
      const isUKStock = ticker.toUpperCase().includes('.L');

      // Fetch latest price from Firestore /marketPrices/{ticker}/history
      const historyRef = collection(db, 'marketPrices', normalizedTicker, 'history');
      const historyQuery = query(historyRef, orderBy('date', 'desc'), limit(1));
      const snapshot = await getDocs(historyQuery);

      if (!snapshot.empty) {
        const latestDoc = snapshot.docs[0];
        const data = latestDoc.data();

        // Check if price is in pence and needs conversion to pounds
        // Yahoo Finance returns UK stock prices in pence with currency 'GBp' or 'GBX'
        // Note: 'GBp' (lowercase p) = pence, 'GBP' (uppercase) = pounds
        const currencyIsPence = data.currency && (data.currency === 'GBp' || data.currency.toUpperCase() === 'GBX');
        // Also convert if UK stock (.L) and no currency specified (assume pence)
        const isPence = currencyIsPence || (isUKStock && !data.currency);
        const conversionFactor = isPence ? 100 : 1;

        setPriceData({
          ticker,
          price: data.price / conversionFactor,
          open: data.open != null ? data.open / conversionFactor : null,
          high: data.high != null ? data.high / conversionFactor : null,
          low: data.low != null ? data.low / conversionFactor : null,
          volume: data.volume || null,
          currency: isPence ? 'GBP' : (data.currency || 'USD'), // Normalize to GBP
          source: data.source,
          timestamp: data.timestamp,
          date: data.date,
          originalCurrency: data.currency, // Keep original for debugging
          wasConverted: isPence
        });
        setLastUpdated(data.timestamp);
      } else {
        // No price data found
        setPriceData(null);
        setError(new Error(`No price data available for ${ticker}`));
      }
    } catch (err) {
      console.error(`❌ [usePricing] Error fetching price for ${ticker}:`, err);
      setError(err);
      setPriceData(null);
    } finally {
      setLoading(false);
    }
  }, [ticker]);

  // Load price on mount and when ticker changes
  useEffect(() => {
    fetchPrice();
  }, [fetchPrice]);

  return {
    priceData,
    loading,
    error,
    lastUpdated,
    refresh: fetchPrice
  };
};

/**
 * Hook for fetching prices for multiple investments (portfolio)
 *
 * @param {Array} investments - Array of investment objects with ticker and category
 * @returns {Object} { prices, loading, error, lastUpdated, refresh }
 */
export const usePortfolioPrices = (investments = []) => {
  const { currentUser } = useAuth();
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchPrices = useCallback(async () => {
    console.log('🔍 [usePricing] fetchPrices called with', investments?.length || 0, 'investments');

    if (!investments || investments.length === 0) {
      console.log('🔍 [usePricing] No investments to fetch prices for');
      setLoading(false);
      setPrices({});
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const priceMap = {};
      let successCount = 0;
      let failCount = 0;
      let latestTimestamp = 0;

      // Fetch prices for each investment
      for (const investment of investments) {
        const rawTicker = investment.ticker;
        const rawISIN = investment.isin;

        // Determine lookup key: ticker takes precedence, fallback to ISIN
        let lookupKey = null;
        let isISINLookup = false;

        if (rawTicker && rawTicker.trim() !== '') {
          lookupKey = rawTicker.toUpperCase().trim();
        } else if (rawISIN && rawISIN.trim() !== '' && /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/.test(rawISIN.toUpperCase().replace(/\s/g, ''))) {
          lookupKey = rawISIN.toUpperCase().replace(/\s/g, '');
          isISINLookup = true;
        }

        if (!lookupKey) {
          console.log('🔍 [usePricing] Skipping investment without ticker/ISIN:', investment.name);
          continue;
        }

        // Check if this is a UK stock (has .L suffix) - prices will be in pence
        const isUKStock = !isISINLookup && lookupKey.includes('.L');

        console.log(`🔍 [usePricing] Fetching price for: key="${lookupKey}", isISIN=${isISINLookup}, isUKStock=${isUKStock}`);

        try {
          // Fetch latest price from Firestore (stored under ticker or ISIN)
          const historyRef = collection(db, 'marketPrices', lookupKey, 'history');
          const historyQuery = query(historyRef, orderBy('date', 'desc'), limit(1));
          console.log(`🔍 [usePricing] Querying: /marketPrices/${lookupKey}/history`);
          const snapshot = await getDocs(historyQuery);

          console.log(`🔍 [usePricing] Query result for ${lookupKey}: empty=${snapshot.empty}, docs=${snapshot.docs.length}`);

          if (!snapshot.empty) {
            const latestDoc = snapshot.docs[0];
            const data = latestDoc.data();

            console.log(`✅ [usePricing] Found price data for ${lookupKey}:`, {
              docId: latestDoc.id,
              price: data.price,
              currency: data.currency,
              date: data.date,
              source: data.source
            });

            // Check if price is in pence and needs conversion to pounds
            // Yahoo Finance returns UK stock prices in pence with currency 'GBp' or 'GBX'
            // Note: 'GBp' (lowercase p) = pence, 'GBP' (uppercase) = pounds
            // ISIN-based funds return prices in GBP (pounds), not pence
            const currencyIsPence = !isISINLookup && data.currency && (data.currency === 'GBp' || data.currency.toUpperCase() === 'GBX');
            // Also convert if UK stock (.L) and no currency specified (assume pence)
            const isPence = currencyIsPence || (isUKStock && !data.currency);
            const conversionFactor = isPence ? 100 : 1;

            console.log(`🔍 [usePricing] Pence conversion for ${lookupKey}: currencyIsPence=${currencyIsPence}, isPence=${isPence}, factor=${conversionFactor}`);

            // Store under original identifier (ticker or ISIN) for lookup
            // For ISIN, store under ISIN; for ticker, store under ticker
            const storageKey = isISINLookup ? rawISIN : rawTicker;
            priceMap[storageKey] = {
              ticker: rawTicker || null,
              isin: rawISIN || null,
              price: data.price / conversionFactor,
              open: data.open != null ? data.open / conversionFactor : null,
              high: data.high != null ? data.high / conversionFactor : null,
              low: data.low != null ? data.low / conversionFactor : null,
              volume: data.volume || null,
              currency: isPence ? 'GBP' : (data.currency || 'USD'), // Normalize to GBP
              source: data.source,
              timestamp: data.timestamp,
              date: data.date,
              originalCurrency: data.currency, // Keep original for debugging
              wasConverted: isPence,
              fundName: data.fundName || null // For ISIN-based funds
            };

            // Track latest timestamp
            if (data.timestamp > latestTimestamp) {
              latestTimestamp = data.timestamp;
            }

            successCount++;
          } else {
            // No price data found for this ticker/ISIN
            console.log(`⚠️ [usePricing] No price data found for ${lookupKey} - snapshot is empty`);
            const storageKey = isISINLookup ? rawISIN : rawTicker;
            priceMap[storageKey] = null;
            failCount++;
          }
        } catch (err) {
          console.error(`❌ [usePricing] Error fetching price for ${lookupKey}:`, err.message);
          const storageKey = isISINLookup ? rawISIN : rawTicker;
          priceMap[storageKey] = null;
          failCount++;
        }
      }

      console.log(`📊 [usePricing] Fetch complete: success=${successCount}, fail=${failCount}`);
      console.log(`📊 [usePricing] Price map keys:`, Object.keys(priceMap));

      // Log the actual prices found
      Object.entries(priceMap).forEach(([key, value]) => {
        if (value) {
          console.log(`📊 [usePricing] Price for ${key}: £${value.price} (currency: ${value.currency}, converted: ${value.wasConverted})`);
        } else {
          console.log(`📊 [usePricing] Price for ${key}: NOT FOUND`);
        }
      });

      setPrices(priceMap);
      setLastUpdated(latestTimestamp || Date.now());

      if (failCount > 0) {
        console.warn(`⚠️ [usePricing] Failed to fetch ${failCount} out of ${investments.length} prices`);
      }
    } catch (err) {
      console.error('❌ [usePricing] Error fetching portfolio prices:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [investments]);

  // Load prices on mount and when investments change
  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  return {
    prices,
    loading,
    error,
    lastUpdated,
    refresh: fetchPrices
  };
};

/**
 * Check if price data is stale (older than 24 hours)
 *
 * @param {number} timestamp - Unix timestamp
 * @returns {boolean} True if stale
 */
export const isPriceStale = (timestamp) => {
  if (!timestamp) return true;

  const ONE_DAY = 24 * 60 * 60 * 1000; // 24 hours in ms
  const age = Date.now() - timestamp;

  return age > ONE_DAY;
};

/**
 * Format timestamp as relative time ("2 hours ago")
 *
 * @param {number} timestamp - Unix timestamp
 * @returns {string} Formatted time
 */
export const formatLastUpdated = (timestamp) => {
  if (!timestamp) return 'Never';

  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 60) return 'Just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;

  const months = Math.floor(days / 30);
  return `${months} month${months > 1 ? 's' : ''} ago`;
};

export default usePortfolioPrices;
